import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  branchesApi,
  financialYearsApi,
  goodsVouchersApi,
  partyAddressesApi,
  voucherTypesApi,
} from '../../lib/api';
import type {
  Branch,
  FinancialYear,
  GoodsBillReferencePayload,
  GoodsDirection,
  GoodsVoucher,
  GoodsVoucherPayload,
  GoodsVoucherRegisterItem,
  Ledger,
  VoucherType,
} from '../../lib/types';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field, Select, TextInput } from '../../components/Field';
import { useUiStore } from '../../stores/ui';
import { useShortcuts } from '../../lib/keys';
import { addMoney, compareMoney, formatIndian, parseAmountInput, ZERO } from '../../lib/money';

type ReturnKind = 'debit_note' | 'credit_note';
type ReturnRow = {
  key: string;
  sourceInventoryEntryId: string;
  itemId: string;
  itemUnitId: string;
  itemName: string;
  unitLabel: string;
  sourceActual: string;
  sourceBilled: string;
  sourceFree: string;
  unitRate: string;
  discountPercent?: string;
  discountAmount?: string;
  discountTradeAmount?: string;
  itcEligibility?: 'eligible' | 'ineligible_section_17_5' | 'capital_goods' | 'input_service';
  actualQuantity: string;
  billedQuantity: string;
  freeQuantity: string;
};

const TODAY = new Date().toISOString().slice(0, 10);
const sourceStart = `${TODAY.slice(0, 4)}-01-01`;
const money = (value: string | null | undefined) => formatIndian(value ?? ZERO);

function text(value: unknown, fallback = ''): string {
  return value === null || value === undefined ? fallback : String(value);
}

function minDecimal(left: string, right: string): string {
  try {
    return Decimal.min(new Decimal(left || 0), new Decimal(right || 0)).toFixed(6);
  } catch {
    return '0.000000';
  }
}

function sourceRows(voucher: GoodsVoucher): ReturnRow[] {
  const facts = Array.isArray(voucher.computed?.lineFacts) ? voucher.computed.lineFacts : [];
  return voucher.inventoryEntries.map((raw, index) => {
    const entry = raw as Record<string, unknown>;
    const lineNumber = Number(entry.line_number ?? entry.lineNumber ?? index + 1);
    const fact = (facts[lineNumber - 1] ?? {}) as Record<string, unknown>;
    const stockQuantity = text(entry.stock_qty_in ?? entry.stock_qty_out ?? fact.actualQuantity, '0');
    const billedQuantity = text(fact.billedQuantity ?? entry.billed_qty_in ?? entry.billed_qty_out, '0');
    const freeQuantity = text(fact.freeQuantity, '0');
    return {
      key: crypto.randomUUID(),
      sourceInventoryEntryId: text(entry.id),
      itemId: text(fact.itemId ?? entry.item_id),
      itemUnitId: text(fact.itemUnitId ?? entry.quantity_uom_id),
      itemName: text(fact.itemName, `Source line ${lineNumber}`),
      unitLabel: text(fact.unitLabel ?? fact.unitCode, 'UOM'),
      sourceActual: `${text(fact.actualQuantity, stockQuantity)} UOM / ${stockQuantity} stock`,
      sourceBilled: billedQuantity,
      sourceFree: freeQuantity,
      unitRate: text(fact.unitRate, '0.000000'),
      discountPercent: fact.discountPercent === undefined ? undefined : text(fact.discountPercent),
      discountAmount: fact.discountAmount === undefined ? undefined : text(fact.discountAmount),
      discountTradeAmount: fact.discountTradeAmount === undefined ? undefined : text(fact.discountTradeAmount),
      itcEligibility: fact.itcEligibility as ReturnRow['itcEligibility'],
      actualQuantity: '',
      billedQuantity: '',
      freeQuantity: '',
    };
  }).filter((row) => row.sourceInventoryEntryId && row.itemId && row.itemUnitId);
}

function billRefsForAmount(bills: GoodsBillReferencePayload[], amount: string): GoodsBillReferencePayload[] {
  let remaining = amount;
  const refs: GoodsBillReferencePayload[] = [];
  for (const bill of bills) {
    if (!bill.id || compareMoney(remaining, ZERO) <= 0) break;
    const take = compareMoney(bill.amount, remaining) < 0 ? bill.amount : remaining;
    if (compareMoney(take, ZERO) > 0) {
      refs.push({
        referenceType: 'against_ref',
        settlesBillReferenceId: bill.id,
        referenceNumber: bill.referenceNumber,
        amount: take,
        dueDate: bill.dueDate,
        narration: 'Physical return against source bill',
      });
      remaining = new Decimal(remaining).minus(new Decimal(take)).toFixed(4);
    }
  }
  return refs;
}

function expectedBillAmount(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const expected = error.details?.expected;
  return typeof expected === 'string' ? expected : null;
}

export function GoodsReturnWorkspacePage({ kind }: { kind: ReturnKind }) {
  const navigate = useNavigate();
  const pushToast = useUiStore((state) => state.pushToast);
  const sourceDirection: GoodsDirection = kind === 'debit_note' ? 'purchase' : 'sales';
  const [types, setTypes] = useState<VoucherType[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [years, setYears] = useState<FinancialYear[]>([]);
  const [register, setRegister] = useState<GoodsVoucherRegisterItem[]>([]);
  const [sourceFrom, setSourceFrom] = useState(sourceStart);
  const [sourceTo, setSourceTo] = useState(TODAY);
  const [sourceId, setSourceId] = useState('');
  const [source, setSource] = useState<GoodsVoucher | null>(null);
  const [party, setParty] = useState<Ledger | null>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [branchId, setBranchId] = useState('');
  const [yearId, setYearId] = useState('');
  const [date, setDate] = useState(TODAY);
  const [sourceBillId, setSourceBillId] = useState('');
  const [narration, setNarration] = useState('');
  const [reference, setReference] = useState('');
  const [previewAmount, setPreviewAmount] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const voucherType = types.find(
    (type) => type.isActive && type.baseBehaviourCode === kind && type.allowsInventoryEntries && type.allowsBillReferences
  );
  const selectedSourceRow = register.find((item) => item.id === sourceId);
  const sourceBills = useMemo(
    () => (source?.billReferences ?? []).filter((bill) => bill.id && ['new_ref', 'advance', 'on_account'].includes(bill.referenceType)),
    [source]
  );
  const sourcePartyId = text(source?.computed && (source.computed as Record<string, unknown>).partyLedgerId) || selectedSourceRow?.partyLedgerId || '';
  const sourceBranch = branches.find((branch) => branch.id === branchId);
  const selectedSourceBill = sourceBills.find((bill) => bill.id === sourceBillId) ?? sourceBills[0];
  const activeRows = rows.filter((row) => compareMoney(parseAmountInput(row.actualQuantity) ?? ZERO, ZERO) > 0);
  const totalSourceValue = sourceBills.reduce((sum, bill) => addMoney(sum, bill.amount), ZERO);

  useEffect(() => {
    let cancelled = false;
    Promise.all([voucherTypesApi.list(), branchesApi.list(), financialYearsApi.list()])
      .then(([typeResponse, branchResponse, yearResponse]) => {
        if (cancelled) return;
        setTypes(typeResponse.items);
        setBranches(branchResponse.items);
        setYears(yearResponse.items);
        setBranchId((current) => current || branchResponse.items.find((item) => item.isPrimary && item.isActive)?.id || branchResponse.items[0]?.id || '');
        setYearId((current) => current || yearResponse.items.find((item) => !item.isClosed)?.id || yearResponse.items[0]?.id || '');
      })
      .catch(setError)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function loadSources() {
    setBusy(true);
    setError(null);
    try {
      const result = await goodsVouchersApi.register(sourceDirection, sourceFrom, sourceTo);
      setRegister(result.items.filter((item) => !item.cancelledAt));
      setSourceId('');
      setSource(null);
      setRows([]);
      setParty(null);
      setPreviewAmount(null);
    } catch (cause) {
      setRegister([]);
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  async function selectSource(id: string) {
    setSourceId(id);
    setSource(null);
    setRows([]);
    setPreviewAmount(null);
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const detail = await goodsVouchersApi.get(id);
      const registerRow = register.find((item) => item.id === id);
      setSource(detail);
      setRows(sourceRows(detail));
      setParty({ id: text((detail.computed as Record<string, unknown> | null)?.partyLedgerId || registerRow?.partyLedgerId), name: registerRow?.partyName || 'Source party', ledgerType: 'party' } as Ledger);
      setBranchId(detail.branchId);
      const returnDate = TODAY >= detail.voucherDate ? TODAY : detail.voucherDate;
      setDate(returnDate);
      setYearId(years.find((year) => !year.isClosed && year.startsOn <= returnDate && year.endsOn >= returnDate)?.id ?? detail.financialYearId);
      const firstBill = detail.billReferences.find((bill) => bill.id && ['new_ref', 'advance', 'on_account'].includes(bill.referenceType));
      setSourceBillId(firstBill?.id ?? '');
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (kind !== 'credit_note' || !sourcePartyId) {
      setAddresses([]);
      return;
    }
    partyAddressesApi.list({ partyLedgerId: sourcePartyId }).then((result) => setAddresses(result.items)).catch(setError);
  }, [kind, sourcePartyId]);

  const payload = useMemo<GoodsVoucherPayload | null>(() => {
    if (!source || !party || !voucherType || !branchId || !yearId || !sourceId) return null;
    const shipping = addresses.find((address) => address.addressKind === 'shipping' || address.address_kind === 'shipping') ?? addresses[0];
    const branchState = sourceBranch?.stateCode ?? '';
    const shared: GoodsVoucherPayload = {
      voucherTypeId: voucherType.id,
      branchId: source.branchId,
      financialYearId: yearId,
      voucherDate: date,
      state: 'regular',
      narration: narration.trim(),
      reference: reference.trim() || null,
      partyLedgerId: sourcePartyId,
      sourceVoucherId: sourceId,
      placeOfSupplyStateCode: kind === 'debit_note' ? branchState : text(shipping?.stateCode ?? shipping?.state_code ?? branchState),
      lines: activeRows.map((row) => ({
        sourceInventoryEntryId: row.sourceInventoryEntryId,
        itemId: row.itemId,
        itemUnitId: row.itemUnitId,
        actualQuantity: parseAmountInput(row.actualQuantity) ?? ZERO,
        billedQuantity: parseAmountInput(row.billedQuantity) ?? ZERO,
        freeQuantity: parseAmountInput(row.freeQuantity) ?? ZERO,
        unitRate: row.unitRate,
        discountPercent: row.discountPercent,
        discountAmount: row.discountAmount,
        discountTradeAmount: row.discountTradeAmount,
        itcEligibility: row.itcEligibility,
      })),
    };
    if (kind === 'debit_note') {
      shared.purchaseType = 'domestic';
      shared.reverseCharge = false;
    } else {
      shared.saleType = 'b2b';
      shared.billingAddressId = text((addresses.find((address) => address.addressKind === 'billing' || address.address_kind === 'billing') ?? addresses[0])?.id);
      shared.shippingAddressId = text(shipping?.id);
    }
    return shared;
  }, [activeRows, addresses, branchId, date, kind, narration, party, reference, source, sourceBranch?.stateCode, sourceId, sourcePartyId, voucherType, yearId]);

  function refsForAmount(amount: string): GoodsBillReferencePayload[] {
    const orderedBills = selectedSourceBill
      ? [selectedSourceBill, ...sourceBills.filter((bill) => bill.id !== selectedSourceBill.id)]
      : sourceBills;
    return billRefsForAmount(orderedBills, amount);
  }

  async function previewReturn() {
    if (!payload || !selectedSourceBill || !activeRows.length) {
      setError(new Error('Choose a source voucher, source bill and at least one return quantity before previewing.'));
      return null;
    }
    let amount = totalSourceValue;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const refs = refsForAmount(amount);
      if (!refs.length) throw new Error('The source voucher has no open bill reference available for Against Ref settlement.');
      try {
        const result = await goodsVouchersApi.previewPhysicalReturn(kind, { ...payload, billReferences: refs });
        const computed = result.computed.computation?.totals?.counterpartyPayable;
        if (!computed) throw new Error('The server preview did not return a counterparty payable amount.');
        setPreviewAmount(computed);
        return { payload: { ...payload, billReferences: refsForAmount(computed) }, amount: computed };
      } catch (cause) {
        const expected = expectedBillAmount(cause);
        if (!expected || expected === amount) throw cause;
        amount = expected;
      }
    }
    throw new Error('The server could not resolve the physical return allocation amount.');
  }

  async function preview() {
    setBusy(true);
    setError(null);
    try {
      await previewReturn();
    } catch (cause) {
      setPreviewAmount(null);
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const prepared = await previewReturn();
      if (!prepared) return;
      const saved = await goodsVouchersApi.createPhysicalReturn(kind, prepared.payload, crypto.randomUUID());
      pushToast({ kind: 'success', message: `${saved.displayNumber} posted as a physical ${kind === 'debit_note' ? 'purchase return' : 'sales return'}.` });
      navigate(`/goods/${kind === 'debit_note' ? 'debit-note' : 'credit-note'}/${saved.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  function patchRow(key: string, patch: Partial<ReturnRow>) {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...patch } : row));
    setPreviewAmount(null);
  }

  useShortcuts(`Physical ${kindLabel(kind)}`, [
    { id: 'preview-return', bindings: [{ key: 'p', ctrl: true }], description: 'Preview physical return', allowInInputs: true, handler: () => void preview() },
    { id: 'save-return', bindings: [{ key: 'a', ctrl: true }], description: 'Post physical return', allowInInputs: true, handler: () => void save() },
  ]);

  if (loading) return <div className="p-5 text-[12.5px] text-[var(--text-dim)]">Loading physical return workspace…</div>;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2">
        <div className="flex items-center gap-2"><button className="text-[12px] text-[var(--accent)]" onClick={() => navigate(`/money/${kind === 'debit_note' ? 'debit-note' : 'credit-note'}/new`)}>← Financial note</button><span className="text-[var(--text-faint)]">/</span><div><p className="mono text-[10px] uppercase tracking-[.16em] text-[var(--accent)]">GOODS RETURN / SOURCE-BOUNDED</p><h1 className="text-[16px] font-semibold">Physical {kindLabel(kind)}</h1></div></div>
        <div className="flex gap-1"><button className="button-muted" onClick={() => void preview()} disabled={busy || !payload}>Preview <span className="kbd">Ctrl+P</span></button><button className="button-primary" onClick={() => void save()} disabled={busy || !payload}>{busy ? 'Working…' : 'Post return'} <span className="kbd">Ctrl+A</span></button></div>
      </header>
      <div className="min-h-0 flex-1 overflow-auto p-3"><div className="mx-auto flex max-w-[1320px] flex-col gap-2">
        {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}
        <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-3"><div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]"><Field label={`Original ${sourceDirection} date range`}><div className="flex gap-1"><TextInput type="date" value={sourceFrom} onChange={(event) => setSourceFrom(event.target.value)} /><TextInput type="date" value={sourceTo} onChange={(event) => setSourceTo(event.target.value)} /></div></Field><Field label="Original voucher" required><Select value={sourceId} onChange={(event) => void selectSource(event.target.value)}><option value="">Choose original {sourceDirection}…</option>{register.map((item) => <option key={item.id} value={item.id}>{item.displayNumber} · {item.partyName} · {money(item.totals?.counterpartyPayable ?? '0')}</option>)}</Select></Field><div className="flex items-end"><button className="button-muted" disabled={busy} onClick={() => void loadSources()}>Load source register</button></div></div><p className="mt-2 text-[11px] text-[var(--text-dim)]">Every line is tied to the original inventory entry. The server freezes source rate/GST/discount facts and rejects cumulative quantity beyond what remains.</p></section>
        {source && <>
          <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-3"><div className="grid gap-2 md:grid-cols-4"><Field label="Party" required><div className="border border-[var(--border)] bg-[var(--bg-inset)] px-2 py-1 text-[13px]">{party?.name || selectedSourceRow?.partyName || 'Source party'}<span className="ml-2 text-[10px] uppercase tracking-wide text-[var(--text-faint)]">locked to source</span></div></Field><Field label="Return date" required><TextInput type="date" value={date} onChange={(event) => { const nextDate = event.target.value; setDate(nextDate); setYearId(years.find((year) => !year.isClosed && year.startsOn <= nextDate && year.endsOn >= nextDate)?.id ?? yearId); }} /></Field><Field label="Source branch" required><div className="border border-[var(--border)] bg-[var(--bg-inset)] px-2 py-1 text-[13px]">{sourceBranch?.name || 'Source branch'}<span className="ml-2 text-[10px] uppercase tracking-wide text-[var(--text-faint)]">locked</span></div></Field><Field label="Financial year" required><Select value={yearId} onChange={(event) => setYearId(event.target.value)}><option value="">Choose FY…</option>{years.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</Select></Field><Field label="Source bill to settle" required><Select value={sourceBillId} onChange={(event) => setSourceBillId(event.target.value)}><option value="">Choose source bill…</option>{sourceBills.map((bill) => <option key={bill.id} value={bill.id}>{bill.referenceNumber || bill.id} · {money(bill.amount)}</option>)}</Select></Field><Field label="Narration"><TextInput value={narration} onChange={(event) => setNarration(event.target.value)} placeholder="Goods returned against source voucher" /></Field><Field label="Reference"><TextInput value={reference} onChange={(event) => setReference(event.target.value)} placeholder="GRN / return reference" /></Field></div></section>
          <section className="border border-[var(--border-strong)] bg-[var(--bg-elevated)]"><div className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--bg-inset)] px-3 py-2"><div><p className="mono text-[10px] uppercase tracking-[.16em] text-[var(--accent)]">SOURCE INVENTORY</p><h2 className="text-[13px] font-semibold">Select return quantities</h2></div><span className="text-[11px] text-[var(--text-dim)]">Blank quantity = not returned</span></div><div className="overflow-auto"><table className="w-full min-w-[980px] text-[12px]"><thead><tr>{['Item / source entry', 'Source qty', 'Return qty', 'Billed qty', 'Free qty', 'Rate', 'Source GST'].map((heading) => <th key={heading} className="table-head">{heading}</th>)}</tr></thead><tbody>{rows.map((row) => <tr key={row.key} className="border-b border-[var(--border)]"><td className="table-cell"><div className="font-medium">{row.itemName}</div><div className="mono text-[10px] text-[var(--text-faint)]">{row.sourceInventoryEntryId.slice(0, 8)} · {row.unitLabel}</div></td><td className="table-cell"><span className="num">{row.sourceActual}</span><span className="ml-1 text-[var(--text-faint)]">({row.sourceBilled} billed)</span></td><td className="table-cell"><TextInput className="w-28 text-right font-mono" inputMode="decimal" value={row.actualQuantity} onChange={(event) => patchRow(row.key, { actualQuantity: event.target.value, billedQuantity: row.billedQuantity || minDecimal(event.target.value, row.sourceBilled) })} placeholder="0.000000" /></td><td className="table-cell"><TextInput className="w-28 text-right font-mono" inputMode="decimal" value={row.billedQuantity} onChange={(event) => patchRow(row.key, { billedQuantity: event.target.value })} placeholder="0.000000" /></td><td className="table-cell"><TextInput className="w-28 text-right font-mono" inputMode="decimal" value={row.freeQuantity} onChange={(event) => patchRow(row.key, { freeQuantity: event.target.value })} placeholder="0.000000" /></td><td className="table-cell num">{money(row.unitRate)}</td><td className="table-cell">{text((source?.computed?.lineFacts?.[rows.indexOf(row)] as Record<string, unknown> | undefined)?.gstRate && ((source?.computed?.lineFacts?.[rows.indexOf(row)] as Record<string, unknown>).gstRate as Record<string, unknown>).ratePercentage, '0')}%</td></tr>)}</tbody></table>{!rows.length && <div className="p-5 text-center text-[12px] text-[var(--text-faint)]">No active inventory entries were returned by the source voucher.</div>}</div></section>
          <section className="flex flex-wrap items-center justify-between gap-2 border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-3 py-2"><div className="text-[11px] text-[var(--text-dim)]">Source bill pool <b>{sourceBills.length}</b> · selected bill <b>{selectedSourceBill?.referenceNumber || '—'}</b> · preview total <b className="num">{previewAmount ? money(previewAmount) : '—'}</b></div><div className="flex gap-1"><button className="button-muted" onClick={() => void preview()} disabled={busy || !activeRows.length}>Preview server total</button><button className="button-primary" onClick={() => void save()} disabled={busy || !activeRows.length || !party || !voucherType}>{busy ? 'Posting…' : `Post ${kind === 'debit_note' ? 'purchase return' : 'sales return'}`}</button></div></section>
        </>}
        {!source && <div className="border border-dashed border-[var(--border-strong)] px-4 py-10 text-center text-[12px] text-[var(--text-faint)]">Load the original {sourceDirection} register, then choose one uncancelled voucher. Physical returns cannot be entered without a source document.</div>}
      </div></div>
    </div>
  );
}

function kindLabel(kind: ReturnKind) {
  return kind === 'debit_note' ? 'Debit Note' : 'Credit Note';
}
